"use client";

import React from "react";
import { Hammer, Layers, LayoutGrid, Lightbulb, Palette, ClipboardCheck, Store } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
const remodelItems = [{
  id: "item-1",
  number: "01",
  title: "Layouts that fit how you cook",
  description: "Moving the sink, range or island, or removing a wall where the structure and code allow. The layout is set before demolition starts."
}, {
  id: "item-2",
  number: "02",
  title: "Stock, semi-custom or custom cabinets",
  description: "Pick the cabinet line that fits your design and budget, from ready-made stock cabinets to fully custom sizes, colors and finishes."
}, {
  id: "item-3",
  number: "03",
  title: "Countertops and backsplash",
  description: "Quartz, granite and other stone chosen at the showroom, with a tile backsplash selected to match."
}, {
  id: "item-4",
  number: "04",
  title: "Lighting, electrical and plumbing",
  description: "Recessed and under-cabinet lighting, dedicated appliance circuits, and new sink, dishwasher and appliance hookups."
}, {
  id: "item-5",
  number: "05",
  title: "Flooring, trim and paint",
  description: "Tile or luxury vinyl plank flooring, trim and paint so the new kitchen ties into the rest of your home."
}, {
  id: "item-6",
  number: "06",
  title: "Permits and city inspections",
  description: "We coordinate plans, pull the building permit and schedule the required city inspections."
}];
const itemIcons = [Hammer, Layers, LayoutGrid, Lightbulb, Palette, ClipboardCheck];
export default function Mastery() {
  const shouldReduceMotion = useReducedMotion();
  return <section data-section-id="91305" id="architecturecabinetrylayoutmastery" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 relative overflow-hidden border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="flex flex-col items-center text-center max-w-3xl mx-auto mb-14 lg:mb-18">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold tracking-tight text-foreground mb-4">
            What your kitchen remodel can include
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">
            One licensed team handles the layout, trades and finishes, and you pick every material in person.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {remodelItems.map((item, i) => {
          const IconComponent = itemIcons[i];
          return <motion.div key={item.id} data-index={i} initial={{
            opacity: 0,
            y: shouldReduceMotion ? 0 : 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: "-50px"
          }} transition={{
            duration: 0.4,
            delay: i * 0.08
          }} className="flex">
                <Card className="flex flex-col justify-between w-full bg-card border-border border shadow-sm hover:shadow-md hover:border-foreground/25 transition-[border-color,box-shadow] rounded-xl relative p-6 sm:p-7 group">
                  <div className="absolute top-0 right-0 w-8 h-8 pointer-events-none">
                    <div className="absolute top-3 right-3 w-2.5 h-2.5 border-t border-r border-border group-hover:border-primary transition-colors" />
                  </div>

                  <div>
                    <div className="flex items-center justify-between gap-4 mb-6">
                      <div className="flex items-center justify-center size-11 rounded-lg bg-secondary text-primary border border-border group-hover:border-primary/50 transition-colors">
                        <IconComponent className="size-5" />
                      </div>
                      <span className="font-mono text-xs font-semibold tracking-wider text-muted-foreground">
                        {item.number}
                      </span>
                    </div>

                    <CardHeader className="p-0 mb-3">
                      <CardTitle className="text-lg sm:text-xl font-serif font-bold text-foreground leading-snug">
                        {item.title}
                      </CardTitle>
                    </CardHeader>

                    <CardContent className="p-0">
                      <CardDescription className="text-sm text-muted-foreground font-sans leading-relaxed">
                        {item.description}
                      </CardDescription>
                    </CardContent>
                  </div>
                </Card>
              </motion.div>;
        })}
        </div>

        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: "-50px"
      }} transition={{
        duration: 0.4
      }} className="mt-10 lg:mt-14 rounded-xl border border-border bg-card shadow-sm p-6 sm:p-8 flex flex-col md:flex-row md:items-center gap-6">
          <div className="flex items-center justify-center size-12 shrink-0 rounded-lg bg-primary text-primary-foreground">
            <Store className="size-6" />
          </div>
          <div className="flex-1">
            <h3 className="text-xl sm:text-2xl font-serif font-bold text-foreground mb-2">
              Choose your materials in person
            </h3>
            <p className="text-sm sm:text-base text-muted-foreground font-sans leading-relaxed">
              We take you to our partner showrooms in Orange County and the Inland Empire to choose cabinets, countertops, backsplash tile, flooring, plumbing fixtures and lighting. Compare stock, semi-custom and custom options side by side before anything is ordered.
            </p>
          </div>
          <a href="https://gn-construction.com/kitchen-remodeling/#schedule" className="inline-flex items-center justify-center rounded-md bg-primary px-6 h-11 text-sm font-semibold text-primary-foreground shadow-sm hover:bg-primary/90 transition-colors shrink-0">
            Start Your Project
          </a>
        </motion.div>
      </div>
    </section>;
}